/**
 * Shared helpers for rendering Lounge comment threads (Disneyland + WDW).
 */

export const getReplyPlacementId = (rep: any) =>
  rep?.id ?? rep?.reply_id ?? rep?.chat_reply_reply_id;

export const getReplyParentId = (rep: any) =>
  rep?.parent_reply_id ?? rep?.reply_parent_id ?? rep?.reply_target_reply_id;

const getReplyUserName = (rep: any): string | undefined =>
  rep?.replyuser?.user_name ?? rep?.user?.user_name;

export type ReplyThreadMeta = {
  /** 1 = direct reply to the comment, 2 = reply to a reply (visual depth is capped at 2). */
  depth: 1 | 2;
  /** Author of the reply this one answers; only set for depth-2 replies. */
  replyingToUserName?: string;
};

/**
 * Annotates replies (already ordered parent-before-child) with their visual
 * nesting depth and the author they are answering.
 */
export const getReplyThreadMeta = (replies: any[] = []): ReplyThreadMeta[] => {
  const byId = new Map<string, any>();
  (replies || []).forEach((rep) => {
    const id = getReplyPlacementId(rep);
    if (id != null) byId.set(String(id), rep);
  });

  return (replies || []).map((rep) => {
    const parentId = getReplyParentId(rep);
    const parent = parentId != null ? byId.get(String(parentId)) : undefined;
    if (!parent || parent === rep) return { depth: 1 };
    return { depth: 2, replyingToUserName: getReplyUserName(parent) };
  });
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Parses backend timestamps like "2026-09-23 10:09:14". The space-separated
 * form is not reliably parsed by older Safari/WKWebView, so normalize it to
 * ISO local time first.
 */
export const parseCommentDate = (value: any): Date | null => {
  if (value == null || value === '') return null;
  const normalized =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d/.test(value)
      ? value.replace(' ', 'T')
      : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "Sep 23 at 10:09 AM" this year, "Sep 23, 2025" for older items. */
export const formatCommentDate = (value: any, now: Date = new Date()) => {
  const date = parseCommentDate(value);
  if (!date) return '';

  const day = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  if (date.getFullYear() !== now.getFullYear()) {
    return `${day}, ${date.getFullYear()}`;
  }

  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${day} at ${hour12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
};

/** Long reply lists show this many replies until the reader expands them. */
export const COLLAPSED_REPLY_COUNT = 2;

/**
 * Returns the replies to render. Lists longer than COLLAPSED_REPLY_COUNT + 1
 * collapse (hiding a single reply behind a button isn't worth the tap).
 */
export const getVisibleReplies = <T,>(replies: T[] = [], expanded: boolean) => {
  if (expanded || replies.length <= COLLAPSED_REPLY_COUNT + 1) {
    return { visible: replies, hiddenCount: 0 };
  }
  return {
    visible: replies.slice(0, COLLAPSED_REPLY_COUNT),
    hiddenCount: replies.length - COLLAPSED_REPLY_COUNT,
  };
};
